export type EstimateStage = 'brief' | 'story' | 'screenplay';
export type TierKey = 'economy' | 'mid' | 'star';
export type PackageKey = TierKey | 'custom';

export interface EstimateLine {
  key: 'clips' | 'stills' | 'writing' | 'audio';
  label: string;
  count: number;
  seconds?: number;
  unitINR: number | null;
  unit: string;
  retake: number;
  totalINR: number;
  model: string;
  source: 'live' | 'fleet-fallback' | 'included' | 'unpriced';
  explain: string;
}

export interface EstimateShot {
  shotId: string;
  sceneId: string | null;
  model: string;
  modelLabel: string;
  resolution: string;
  seconds: number;
  units: number;
  unitINR: number;
  retake: number;
  expectedINR: number;
  approved: boolean;
  dialogue: boolean;
  forcedLongTake: boolean;
  source: string;
}

export interface EstimatePackage {
  id: PackageKey;
  label: string;
  eyebrow: string;
  featured: boolean;
  basedOn?: TierKey;
  models: { llm: string; image: string; video: string; resolution: string };
  modelLabels: { llm: string; image: string; video: string };
  expectedINR: number;
  lowINR: number;
  highINR: number;
  videoINR: number;
  stillsINR: number;
  writingINR: number;
  lines: EstimateLine[];
  perShot: EstimateShot[];
  maxJobINR: number;
  retake: number;
  estimateOnly?: boolean;
  deltaVs: Partial<Record<TierKey, number>>;
  target: { targetINR: number; varianceINR: number; overTarget: boolean } | null;
  draftSavingINR?: number;
}

export interface PriceBasis {
  source: string;
  provider: string;
  capturedAt: string | null;
  ageMs: number | null;
  fxINRPerUSD: number;
  fxSource: string;
  live: boolean;
  fallbackModels: string[];
}

export interface EstimateOption {
  id: string;
  label: string;
  unitINR?: number;
  unit?: string;
  resolution?: string;
  source: string;
  arena?: number | null;
  maxSeconds?: number;
  audio?: boolean;
  estimateOnly?: boolean;
  inputPerMillion?: number | null;
  outputPerMillion?: number | null;
}

export interface FilmEstimate {
  version: string;
  stage: EstimateStage;
  confidence: 'rough' | 'shaped' | 'locked';
  confidenceLabel: string;
  bandPct: number;
  runtimeSeconds: number;
  aspectRatio: string;
  genre: string;
  quantities: {
    clips: number;
    clipSeconds: number;
    typicalClipSeconds: number;
    stills: number;
    identitySheets: number;
    storyboardStills: number;
    characters: number;
    locations: number;
    scenes: number;
    dialogueShots: number;
    longTakes: number;
    shotsPerMinute: number;
  };
  selectedPackage: PackageKey;
  recommendedPackage: TierKey;
  draftThenFinal: boolean;
  overridden?: Partial<Record<TierKey, EstimatePackage>>;
  packages: Record<TierKey, EstimatePackage> & { custom?: EstimatePackage };
  perShot: EstimateShot[];
  shotDeltas: Record<string, number>;
  sceneTotals: Record<string, number>;
  meter: { targetINR: number | null; spentINR: number; forecastINR: number; projectedINR: number; varianceINR: number | null; overTarget: boolean };
  priceBasis: PriceBasis;
  calibrated: { films: number } | null;
  options: { llm: EstimateOption[]; image: EstimateOption[]; video: EstimateOption[] };
  explain: string[];
}

export interface BriefEstimateInput {
  runtimeMinutes: number;
  aspectRatio: string;
  type: string;
  genre?: string;
  lookPreset?: string;
  targetINR?: number;
  selectedPackage?: string;
  overrides?: Partial<Record<TierKey, { llm?: string; image?: string; video?: string; resolution?: string }>>;
}

export interface FilmPlan {
  id: string;
  status: 'active' | 'expired' | 'revoked' | 'superseded' | 'missing';
  packageId: string;
  ceilingInr: number;
  usedInr: number;
  remainingInr: number;
  maxJobInr: number;
  maxAttemptsPerTask: number;
  attempts: Record<string, number>;
  writing: { allowanceInr: number; usedInr: number; remainingInr: number };
  expiresAt: string;
  approvedBy: string;
  approvedAt: string;
  supersedes: string | null;
  currency: 'INR';
}

export interface CostReportRow { stage: EstimateStage; at: string; package: string; expectedINR: number; lowINR: number; highINR: number; actualINR: number; varianceINR: number; accuracyPct: number | null; withinBand: boolean }
export interface CostReportLine { key: string; label: string; estimatedINR: number; actualINR: number; varianceINR: number }
export interface CostReport {
  report: { actualINR: number; spend?: { draftINR: number; finalINR: number; draftVideoINR: number; finalVideoINR: number }; rows: CostReportRow[]; lines: CostReportLine[]; basedOn: string | null };
  observed: { video: Record<string, { shots: number; takes: number }>; still: { shots: number; takes: number } };
  estimate: FilmEstimate;
}
export interface WritingPrice { inr: number; upperInr: number; model: string; provider: string; source: string; operations: string[] }
export interface GenerationPrice { modelId: string; provider: string; inr: number; upperInr: number; externalCredits: boolean; bounded: boolean; overTarget: { byINR: number; targetINR: number } | null; expensive: boolean; draft?: { model: string; resolution: string } }

export interface DraftOption { id: string; label: string; description: string; resolution: string; default: boolean; audioToggle: boolean; firstFrameOnly: boolean; maxSeconds: number | null; usdPerSecond: number | null; inrPerSecond: number | null; inputImageInr: number; available: boolean; reason: string }

export interface DraftOptions { options: DraftOption[]; usable: boolean; reason: string }

export interface EstimateCalibration { video: Record<string, number>; still?: number; films: number; shots: number }

export const TIER_ORDER: TierKey[] = ['economy', 'mid', 'star'];

export function inr(value: number): string {
  return `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
}

export function inrRange(pkg: Pick<EstimatePackage, 'lowINR' | 'highINR'>): string {
  return `${inr(pkg.lowINR)} – ${inr(pkg.highINR)}`;
}

export function bandLabel(estimate: Pick<FilmEstimate, 'confidenceLabel' | 'bandPct'>): string {
  return `${estimate.confidenceLabel} ±${Math.round(estimate.bandPct * 100)}%`;
}

function ageLabel(ms: number | null): string {
  if (ms == null) return '';
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} h ago`;
}

export function priceBasisLabel(basis: PriceBasis): string {
  const fx = `₹/$ ${Number(basis.fxINRPerUSD).toFixed(2).replace(/\.00$/, '')}`;
  if (basis.live) return ['Live', basis.provider, ageLabel(basis.ageMs), fx].filter(Boolean).join(' · ');
  if (basis.capturedAt) return ['Partly live', basis.provider, ageLabel(basis.ageMs), `${basis.fallbackModels.length} fallback rate${basis.fallbackModels.length === 1 ? '' : 's'}`, fx].filter(Boolean).join(' · ');
  return `Studio fallback rates · live list unavailable · ${fx}`;
}

const CONFIDENCE_WORD: Record<FilmEstimate['confidence'], string> = { rough: 'Rough estimate', shaped: 'Shaped estimate', locked: 'Firm estimate' };
const CONFIDENCE_SOURCE: Record<FilmEstimate['confidence'], string> = { rough: 'from the brief', shaped: 'from the story', locked: 'from the shot list' };

export function estimateConfidence(estimate: Pick<FilmEstimate, 'confidence' | 'bandPct'>): string {
  return `${CONFIDENCE_WORD[estimate.confidence] || 'Estimate'} ±${Math.round(estimate.bandPct * 100)}% · ${CONFIDENCE_SOURCE[estimate.confidence] || 'from the brief'}`;
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

export function estimateScope(estimate: Pick<FilmEstimate, 'quantities'>): string {
  const { clips, characters, locations } = estimate.quantities;
  return [plural(clips, 'clip'), plural(characters, 'character'), plural(locations, 'location')].join(' · ');
}

export function priceSourceLabel(basis: PriceBasis): string {
  const fx = `$1 = ₹${Number(basis.fxINRPerUSD).toFixed(2).replace(/\.00$/, '')}`;
  const age = ageLabel(basis.ageMs);
  if (basis.live) return [`Live prices from ${basis.provider}`, age && `checked ${age}`, fx].filter(Boolean).join(' · ');
  if (basis.capturedAt) return [`Prices from ${basis.provider}`, age && `checked ${age}`, `${plural(basis.fallbackModels.length, 'model')} on studio rates`, fx].filter(Boolean).join(' · ');
  return `Studio rates · the live price list is unavailable · ${fx}`;
}

export function chosenPackage(estimate: FilmEstimate): EstimatePackage {
  return estimate.packages[estimate.selectedPackage as TierKey] || estimate.packages.custom || estimate.packages[estimate.recommendedPackage];
}

export function budgetSummary(estimate: FilmEstimate, plan: FilmPlan | null): { active: boolean; label: string; value: string } {
  if (plan?.status === 'active') {
    const tier = estimate.packages[plan.packageId as TierKey]?.label || 'Custom';
    return { active: true, label: `${tier} plan`, value: `${inr(plan.remainingInr)} left of ${inr(plan.ceilingInr)}` };
  }
  const pkg = chosenPackage(estimate);
  return { active: false, label: 'No plan yet', value: `${pkg.label} ≈ ${inr(pkg.expectedINR)}` };
}

export function targetChip(pkg: EstimatePackage): { text: string; over: boolean } | null {
  if (!pkg.target) return null;
  const amount = Math.abs(pkg.target.varianceINR);
  return pkg.target.overTarget ? { text: `${inr(amount)} over target`, over: true } : { text: `${inr(amount)} under target`, over: false };
}
